import { Bell, BookOpenText, Bookmark, BriefcaseBusiness, ChevronDown, CircleHelp, ExternalLink, FileText, Handshake, Landmark, LogOut, Menu, PenLine, Plus, Scale, Search, Settings, SlidersHorizontal, UserRound, UsersRound, Vote, X } from "lucide-react";
import { useMutation } from "convex/react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MosaicLogo } from "./ui";
import { CreatePostModal, type CreatePostPersistence } from "./CreatePostModal";
import { WorkspaceDetailModals, type AttachmentPersistence, type DiscussionPersistence, type ModerationPersistence } from "./WorkspaceDetailModals";
import { convexApi } from "../convexApi";
import { canUsePublicSiteEditor, canViewAdmin, isObserverUser } from "../lib/domain";
import { createDashboardViewModel } from "../lib/dashboard";
import { resetDemoViewerId } from "../lib/demoSession";
import { appVersionLabel } from "../lib/version";
import type { LexData, PostType } from "../lib/types";

type NavItem = { to: string; label: string; icon: typeof BookOpenText; end?: boolean; countKey?: "requests_to_my_firm_count"; observerHidden?: boolean };

const navItems: NavItem[] = [
  { to: "/discussions", label: "Dashboard", icon: BookOpenText, end: true },
  { to: "/network-projects", label: "Network projects", icon: Vote },
  { to: "/expertise-exchange", label: "Expertise exchange", icon: Handshake, countKey: "requests_to_my_firm_count", observerHidden: true },
  { to: "/legislative-action", label: "Legislative action", icon: Scale },
  { to: "/members-directory", label: "Members directory", icon: UsersRound, observerHidden: true },
  { to: "/governance", label: "Governance", icon: Landmark, observerHidden: true },
  { to: "/my-workspace", label: "My workspace", icon: FileText, observerHidden: true },
];

const createMenuItems = [
  { label: "Question", to: "/discussions?compose=1&type=Question" },
  { label: "Project proposal", to: "/discussions?compose=1&type=Proposal" },
  { label: "Expertise request", to: "/expertise-exchange?compose=1&type=expert_input" },
  { label: "Recommendation request", to: "/expertise-exchange?compose=1&type=ask_view" },
  { label: "Legislative action proposal", to: "/legislative-action?compose=1&type=Proposal" },
  { label: "Resource", to: "/discussions?compose=1&type=Resource" },
  { label: "Advisory poll", to: "/discussions?compose=1&type=Poll" },
];

type QuickAccessPin = { href: string; label: string };

type QuickAccessPersistence = {
  pinPage: (pin: QuickAccessPin) => Promise<unknown>;
  removePin: (href: string) => Promise<unknown>;
};

type Density = "Comfortable" | "Compact";
type Accent = "Burgundy" | "Ink";

const quickAccessStorageKey = "lexmosaic.quickAccessPins";
const displayStorageKey = "lexmosaic.displayTweaks";
const builtInQuickAccessHrefs = ["/my-workspace?view=following", "/my-workspace?view=drafts", "/my-workspace?view=actions"];

export function AppShell({ children, data, mode }: { children: ReactNode; data: LexData; mode: "convex" | "demo" }) {
  if (mode === "convex") return <LiveAppShell data={data}>{children}</LiveAppShell>;
  return <AppShellContent data={data} mode={mode}>{children}</AppShellContent>;
}

function LiveAppShell({ children, data }: { children: ReactNode; data: LexData }) {
  const pinQuickAccessPage = useMutation(convexApi.quickAccess.pinPage);
  const removeQuickAccessPage = useMutation(convexApi.quickAccess.removePin);
  const createDiscussion = useMutation(convexApi.discussions.create);
  const createProposal = useMutation(convexApi.governance.createProposal);
  const quickAccessPersistence = useMemo<QuickAccessPersistence>(
    () => ({
      pinPage: (pin) => pinQuickAccessPage(pin),
      removePin: (href) => removeQuickAccessPage({ href }),
    }),
    [pinQuickAccessPage, removeQuickAccessPage],
  );
  const createPostPersistence = useMemo<CreatePostPersistence>(
    () => ({
      createPost: (input) =>
        input.postType === "Proposal"
          ? createProposal({
              title: input.title,
              body: input.body,
              proposalText: input.body,
              category: "ordinary",
              thresholdType: "simple",
              practiceAreaIds: input.practiceAreaIds as never[],
              topicTags: input.topicTags,
              jurisdictionTags: input.jurisdictionTags,
              confidentialityLevel: input.confidentialityLevel,
              actionLabels: input.actionLabels,
              visibility: input.visibility,
            })
          : createDiscussion(input),
    }),
    [createDiscussion, createProposal],
  );
  const generateUploadUrl = useMutation(convexApi.attachments.generateUploadUrl);
  const addDiscussionAttachment = useMutation(convexApi.attachments.addDiscussionAttachment);
  const removeAttachment = useMutation(convexApi.attachments.removeAttachment);
  const attachmentPersistence = useMemo<AttachmentPersistence>(
    () => ({
      generateUploadUrl: () => generateUploadUrl({}) as Promise<string>,
      addDiscussionAttachment: (input) => addDiscussionAttachment({ discussionId: input.discussionId as never, storageId: input.storageId as never, filename: input.filename, contentType: input.contentType, size: input.size }),
      removeAttachment: (attachmentId) => removeAttachment({ attachmentId: attachmentId as never }),
    }),
    [generateUploadUrl, addDiscussionAttachment, removeAttachment],
  );
  const removeDiscussion = useMutation(convexApi.admin.removeDiscussion);
  const removeContribution = useMutation(convexApi.admin.removeContribution);
  const moderationPersistence = useMemo<ModerationPersistence>(
    () => ({
      removeDiscussion: (discussionId) => removeDiscussion({ discussionId: discussionId as never }),
      removeContribution: (contributionId) => removeContribution({ contributionId: contributionId as never }),
    }),
    [removeDiscussion, removeContribution],
  );
  const addContribution = useMutation(convexApi.discussions.addContribution);
  const closeDiscussion = useMutation(convexApi.discussions.closeDiscussion);
  const openProposalWorkflow = useMutation(convexApi.governance.openProposalWorkflowFromDiscussion);
  const scheduleProposalVote = useMutation(convexApi.governance.scheduleVote);
  const discussionPersistence = useMemo<DiscussionPersistence>(
    () => ({
      addContribution: (input) => addContribution({ discussionId: input.discussionId as never, type: "comment", body: input.body, parentContributionId: (input.parentContributionId ?? null) as never }),
      closeDiscussion: (input) => closeDiscussion({ discussionId: input.discussionId as never, closed: input.closed }),
      pushProposalToVote: async (input) => {
        const opened = await openProposalWorkflow({
          discussionId: input.discussionId as never,
          proposalText: input.proposalText,
          category: "ordinary",
          thresholdType: "simple",
        }) as { proposalId: string };
        await scheduleProposalVote({
          proposalId: opened.proposalId as never,
          votingOpensAt: Date.now() - 1000,
          durationDays: 14,
        });
        return { proposalId: String(opened.proposalId) };
      },
    }),
    [addContribution, closeDiscussion, openProposalWorkflow, scheduleProposalVote],
  );
  return (
    <AppShellContent data={data} mode="convex" quickAccessPersistence={quickAccessPersistence} createPostPersistence={createPostPersistence} attachmentPersistence={attachmentPersistence} moderationPersistence={moderationPersistence} discussionPersistence={discussionPersistence}>
      {children}
    </AppShellContent>
  );
}

function AppShellContent({ children, data, mode, quickAccessPersistence, createPostPersistence, attachmentPersistence, moderationPersistence, discussionPersistence }: { children: ReactNode; data: LexData; mode: "convex" | "demo"; quickAccessPersistence?: QuickAccessPersistence; createPostPersistence?: CreatePostPersistence; attachmentPersistence?: AttachmentPersistence; moderationPersistence?: ModerationPersistence; discussionPersistence?: DiscussionPersistence }) {
  const location = useLocation();
  const navigate = useNavigate();
  const observer = isObserverUser(data.viewer);
  const [searchParams, setSearchParams] = useSearchParams();
  const [notice, setNotice] = useState<string | null>(null);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [postMenuOpen, setPostMenuOpen] = useState(false);
  const [tweakOpen, setTweakOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [display, setDisplay] = useState<{ density: Density; accent: Accent }>(() => readDisplayTweaks());
  const [quickPins, setQuickPins] = useState<QuickAccessPin[]>(() => (mode === "convex" ? quickPinsFromData(data) : readQuickAccessPins()));
  const avatarRef = useRef<HTMLDivElement>(null);
  const postMenuRef = useRef<HTMLDivElement>(null);
  const tweakRef = useRef<HTMLDivElement>(null);
  const dashboard = useMemo(() => createDashboardViewModel(data), [data]);
  const sidebarCounts = dashboard.sidebar_counts;
  const bottomChip = dashboard.bottom_action_chip;
  const unread = data.notifications.filter((item) => !item.readAt).length;
  const initials = data.viewer.name.split(" ").map((part) => part[0]).join("").slice(0, 2);
  const workspaceName = data.viewerFirm?.name ?? "LexMosaic network";
  const workspaceInitials = data.viewerFirm?.name?.slice(0, 2).toUpperCase() ?? "LM";
  const workspaceMeta = data.viewerFirm ? `${data.viewerFirm.membershipCategory} · ${data.users.length} members` : `Admin workspace · ${data.users.length} members`;
  const currentHref = `${location.pathname}${location.search}`;
  const currentPageCanBePinned = canPinQuickAccessPage(location.pathname, location.search);
  const currentPagePinned = quickPins.some((pin) => pin.href === currentHref);
  const searchResults = useMemo(() => searchPrivateWorkspace(data, searchQuery, observer), [data, searchQuery, observer]);

  const appClass = ["app", display.density === "Compact" ? "density-compact" : "", display.accent === "Ink" ? "accent-ink" : ""].filter(Boolean).join(" ");

  const composeOpen = searchParams.get("compose") === "1";
  const composeType = (searchParams.get("type") as PostType | null) ?? "Question";
  const composeDraftId = searchParams.get("draft");
  // Expertise/cooperation routes own ?compose themselves (CooperationRequestModal),
  // so the global Create-post modal must not also open there.
  const onCooperationRoute = location.pathname.startsWith("/expertise-exchange") || location.pathname.startsWith("/cooperation");
  const postComposeOpen = composeOpen && !onCooperationRoute;
  const closeCompose = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("compose");
    next.delete("type");
    next.delete("draft");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (mode === "convex") setQuickPins(quickPinsFromData(data));
  }, [data, mode]);

  useEffect(() => {
    if (!avatarOpen && !postMenuOpen && !tweakOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(event.target as Node)) setAvatarOpen(false);
      if (postMenuRef.current && !postMenuRef.current.contains(event.target as Node)) setPostMenuOpen(false);
      if (tweakRef.current && !tweakRef.current.contains(event.target as Node)) setTweakOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setAvatarOpen(false);
        setPostMenuOpen(false);
        setTweakOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [avatarOpen, postMenuOpen, tweakOpen]);

  useEffect(() => {
    const openSearch = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === "Escape") setSearchOpen(false);
    };
    document.addEventListener("keydown", openSearch);
    return () => document.removeEventListener("keydown", openSearch);
  }, []);

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  const updateDisplay = (next: Partial<{ density: Density; accent: Accent }>) => {
    setDisplay((current) => {
      const merged = { ...current, ...next };
      writeDisplayTweaks(merged);
      return merged;
    });
  };

  const signOut = () => {
    setAvatarOpen(false);
    if (window.Clerk?.signOut) {
      void window.Clerk.signOut().finally(() => window.location.assign("/"));
      return;
    }
    if (mode === "demo") {
      resetDemoViewerId();
      window.location.assign("/discussions");
      return;
    }
    window.location.assign("/");
  };

  const addQuickAccessPin = () => {
    if (!currentPageCanBePinned || currentPagePinned) return;
    const pin = { href: currentHref, label: labelForQuickAccessPage(location.pathname, location.search) };
    const next = [pin, ...quickPins].slice(0, 8);
    setQuickPins(next);
    if (quickAccessPersistence) void quickAccessPersistence.pinPage(pin).catch(() => setQuickPins(quickPinsFromData(data)));
    else writeQuickAccessPins(next);
  };

  const removeQuickAccessPin = (href: string) => {
    const next = quickPins.filter((pin) => pin.href !== href);
    setQuickPins(next);
    if (quickAccessPersistence) void quickAccessPersistence.removePin(href).catch(() => setQuickPins(quickPinsFromData(data)));
    else writeQuickAccessPins(next);
  };

  const chipClass = bottomChip.status === "green" ? "is-green" : bottomChip.status === "amber" ? "is-amber" : "";

  return (
    <div className={appClass}>
      {drawerOpen ? <button aria-label="Close navigation" className="drawer-overlay" onClick={() => setDrawerOpen(false)} /> : null}
      <aside className={`side${drawerOpen ? " drawer-open" : ""}`}>
        <div className="brandrow">
          <MosaicLogo size={30} />
          <span className="wordmark">Lex<b>Mosaic</b></span>
        </div>
        <div className="tagline">Connecting independent legal expertise</div>

        <button type="button" className="wsswitch" title={workspaceName}>
          <span className="wsbadge">{workspaceInitials}</span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span className="wsname" style={{ display: "block" }}>{workspaceName}</span>
            <span className="wsmeta">{workspaceMeta}</span>
          </span>
          <ChevronDown style={{ width: 14, height: 14, color: "var(--muted)" }} />
        </button>

        <nav className="navsec">
          {navItems.filter((item) => !observer || !item.observerHidden).map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `navlink${isActive ? " nav-on" : ""}`}>
              <item.icon />
              <span className="navlabel">{item.label}</span>
              {item.countKey && sidebarCounts[item.countKey] > 0 ? <span className="navcount">{sidebarCounts[item.countKey]}</span> : null}
            </NavLink>
          ))}
        </nav>

        {observer ? null : (
          <>
            <div className="seclabel">Quick access</div>
            <nav className="navsec">
              <QuickLink to="/my-workspace?view=following" icon={Bookmark} label="Saved & followed" count={sidebarCounts.saved_threads_count} />
              <QuickLink to="/my-workspace?view=drafts" icon={PenLine} label="Drafts" count={sidebarCounts.drafts_count} />
              <QuickLink to="/my-workspace?view=actions" icon={Vote} label="Votes due" count={sidebarCounts.open_votes_count} />
              {quickPins.map((pin) => <PinnedQuickLink key={pin.href} pin={pin} onRemove={() => removeQuickAccessPin(pin.href)} />)}
            </nav>
          </>
        )}

        {observer ? null : (
          <Link to={bottomChip.href} className={`statuschip ${chipClass}`}>
            <span className="dot" />
            <span style={{ flex: 1 }}>{bottomChip.label}</span>
            {bottomChip.count > 0 ? <span className="mono" style={{ opacity: 0.7 }}>{bottomChip.count}</span> : null}
          </Link>
        )}
        <div className="appversion mono">{appVersionLabel()}</div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button type="button" className="iconbtn menubtn" aria-label="Open navigation" onClick={() => setDrawerOpen(true)}>
            <Menu />
          </button>
          <button type="button" className="searchbtn" onClick={() => setSearchOpen(true)}>
            <Search />
            <span>Search projects, expertise, members, legislative action…</span>
            <span className="kbd mono">Ctrl K</span>
          </button>

          {currentPageCanBePinned ? (
            <button type="button" className="iconbtn" aria-label={currentPagePinned ? "Pinned" : "Pin page"} title={currentPagePinned ? "Pinned" : "Pin page"} onClick={addQuickAccessPin} disabled={currentPagePinned} style={currentPagePinned ? { color: "var(--accent)" } : undefined}>
              <Bookmark />
            </button>
          ) : null}

          {observer ? null : (
            <div ref={postMenuRef} style={{ position: "relative" }}>
              <button type="button" className="createbtn" aria-haspopup="menu" aria-expanded={postMenuOpen} onClick={() => setPostMenuOpen((open) => !open)}>
                <Plus />
                Create
              </button>
              {postMenuOpen ? (
                <div className="tweakpop" role="menu" style={{ width: 220 }}>
                  {createMenuItems.map((item) => (
                    <Link key={item.label} to={item.to} role="menuitem" onClick={() => setPostMenuOpen(false)} className="btn-sm" style={{ width: "100%", justifyContent: "flex-start", border: "none", marginBottom: 2 }}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          )}

          <div ref={tweakRef} style={{ position: "relative" }}>
            <button type="button" className="iconbtn" aria-label="Display settings" title="Display settings" aria-haspopup="menu" aria-expanded={tweakOpen} onClick={() => setTweakOpen((open) => !open)}>
              <SlidersHorizontal />
            </button>
            {tweakOpen ? (
              <div className="tweakpop" role="menu">
                <div className="tweakgroup">
                  <div className="tweaklabel">Density</div>
                  <div className="tweakrow">
                    {(["Comfortable", "Compact"] as Density[]).map((value) => (
                      <button key={value} type="button" className={`tweakopt${display.density === value ? " tweakopt-on" : ""}`} onClick={() => updateDisplay({ density: value })}>{value}</button>
                    ))}
                  </div>
                </div>
                <div className="tweakgroup">
                  <div className="tweaklabel">Accent</div>
                  <div className="tweakrow">
                    {(["Burgundy", "Ink"] as Accent[]).map((value) => (
                      <button key={value} type="button" className={`tweakopt${display.accent === value ? " tweakopt-on" : ""}`} onClick={() => updateDisplay({ accent: value })}>{value}</button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <Link to="/notifications" className="iconbtn" aria-label="Notifications">
            <Bell />
            {unread > 0 ? <span className="notifdot" /> : null}
          </Link>

          <div ref={avatarRef} style={{ position: "relative" }}>
            <button type="button" className="avatar" style={{ overflow: "hidden", padding: 0 }} aria-haspopup="menu" aria-expanded={avatarOpen} aria-label="Account menu" onClick={() => setAvatarOpen((open) => !open)}>
              {data.viewer.avatarUrl ? <img src={data.viewer.avatarUrl} alt={data.viewer.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}
            </button>
            {avatarOpen ? (
              <div className="tweakpop" role="menu" style={{ width: 248 }}>
                <div style={{ padding: "4px 8px 10px", borderBottom: "1px solid var(--line)", marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, color: "var(--ink)" }}>{data.viewer.name}</div>
                  <div className="wsmeta">{data.viewerFirm?.name}</div>
                </div>
                <MenuLink to="/profile" icon={UserRound} label="Profile and badges" onClick={() => setAvatarOpen(false)} />
                {observer ? null : <MenuLink to="/my-workspace/delegations" icon={BriefcaseBusiness} label="Procurations" onClick={() => setAvatarOpen(false)} />}
                <MenuLink to="/notifications" icon={Bell} label="Notifications" onClick={() => setAvatarOpen(false)} />
                <MenuLink to="/help" icon={CircleHelp} label="Help" onClick={() => setAvatarOpen(false)} />
                <MenuExternalLink href="https://lexmosaic.eu" icon={ExternalLink} label="Public site" onClick={() => setAvatarOpen(false)} />
                {canViewAdmin(data.viewer) ? <MenuLink to="/admin" icon={Settings} label="Admin" onClick={() => setAvatarOpen(false)} /> : null}
                {!canViewAdmin(data.viewer) && canUsePublicSiteEditor(data.viewer) ? <MenuLink to="/admin/public-site" icon={Settings} label="Public site editor" onClick={() => setAvatarOpen(false)} /> : null}
                <button type="button" className="btn-sm" style={{ width: "100%", justifyContent: "flex-start", border: "none" }} role="menuitem" onClick={signOut}>
                  <LogOut style={{ width: 16, height: 16 }} />
                  {mode === "demo" ? "Reset demo session" : "Sign out"}
                </button>
              </div>
            ) : null}
          </div>
        </header>

        {searchOpen ? (
          <div className="drawer-overlay" style={{ zIndex: 60, padding: "80px 16px", display: "block", overflowY: "auto" }} role="dialog" aria-modal="true" onClick={() => setSearchOpen(false)}>
            <div style={{ maxWidth: 640, margin: "0 auto", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 12, boxShadow: "0 24px 60px rgba(11,31,58,0.2)" }} onClick={(event) => event.stopPropagation()}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid var(--line)", padding: "12px 16px" }}>
                <Search style={{ width: 16, height: 16, color: "var(--muted)" }} />
                <input autoFocus value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search members, projects, votes, expertise requests and legislation" style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 17, color: "var(--ink)", fontFamily: "inherit" }} />
                <button type="button" className="iconbtn" aria-label="Close search" onClick={() => setSearchOpen(false)}><X /></button>
              </div>
              <div style={{ maxHeight: "60vh", overflowY: "auto", padding: 8 }}>
                {searchResults.length > 0 ? searchResults.map((result) => (
                  <button key={`${result.kind}-${result.href}`} type="button" className="feeditem" style={{ width: "100%", textAlign: "left", cursor: "pointer", border: "none", background: "none" }} onClick={() => { setSearchOpen(false); setSearchQuery(""); navigate(result.href); }}>
                    <result.icon style={{ width: 16, height: 16, color: "var(--accent)", marginTop: 2, flex: "none" }} />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span className="feed-title" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{result.title}</span>
                      <span className="feed-meta">{result.kind} · {result.detail}</span>
                    </span>
                  </button>
                )) : (
                  <div style={{ padding: "32px 12px", textAlign: "center", color: "var(--muted)", fontSize: 17 }}>No matching private workspace items.</div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <div className="content">{children}</div>
      </div>

      {postComposeOpen ? (
        <CreatePostModal
          data={data}
          initialType={composeType}
          draftId={composeDraftId}
          onClose={closeCompose}
          onCreated={(message) => setNotice(message)}
          persistence={createPostPersistence}
        />
      ) : null}

      <WorkspaceDetailModals data={data} attachmentPersistence={attachmentPersistence} moderationPersistence={moderationPersistence} discussionPersistence={discussionPersistence} />

      {notice ? (
        <div className="toast" role="status">
          <span style={{ flex: 1 }}>{notice}</span>
          <button type="button" className="iconbtn" aria-label="Dismiss" onClick={() => setNotice(null)}>
            <X />
          </button>
        </div>
      ) : null}
    </div>
  );
}

type SearchResult = { kind: string; title: string; detail: string; href: string; icon: typeof Search };

function searchPrivateWorkspace(data: LexData, query: string, observer: boolean): SearchResult[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  const matches = (value: string) => terms.every((term) => value.toLowerCase().includes(term));
  const rows: SearchResult[] = [];

  // Observers can't reach the members directory or expertise exchange, so keep those out of search.
  if (!observer) for (const firm of data.firms) {
    const areas = data.firmPracticeAreas
      .filter((link) => link.firmId === firm.id)
      .map((link) => data.practiceAreas.find((area) => area.id === link.practiceAreaId)?.name)
      .filter(Boolean)
      .join(" ");
    const haystack = `${firm.name} ${firm.jurisdiction} ${firm.membershipCategory} ${areas} ${(firm.languages ?? []).join(" ")}`;
    if (matches(haystack)) rows.push({ kind: "Member", title: firm.name, detail: `${firm.jurisdiction} · ${firm.membershipCategory}`, href: `/members/${firm.id}`, icon: UsersRound });
  }
  for (const discussion of data.discussions) {
    const haystack = `${discussion.title} ${discussion.body} ${(discussion.topicTags ?? []).join(" ")} ${discussion.jurisdictionTags.join(" ")}`;
    if (matches(haystack)) rows.push({ kind: discussion.postType ?? "Project", title: discussion.title, detail: discussion.workflowStatus ?? discussion.status, href: `/discussions?discussion=${discussion.id}`, icon: BookOpenText });
  }
  for (const proposal of data.proposals) {
    const discussion = data.discussions.find((item) => item.id === proposal.discussionId);
    const title = discussion?.title ?? proposal.proposalText;
    if (matches(`${title} ${proposal.status} ${proposal.category}`)) rows.push({ kind: "Vote", title, detail: proposal.status.replace(/_/g, " "), href: `/discussions/proposals/${proposal.id}`, icon: Vote });
  }
  if (!observer) for (const request of data.cooperationRequests) {
    const target = data.firms.find((firm) => firm.id === request.targetFirmId);
    if (matches(`${request.title} ${request.shortSummary} ${request.status} ${target?.name ?? ""}`)) rows.push({ kind: "Expertise exchange", title: request.title, detail: `${target?.name ?? "Member firm"} · ${request.status.replace(/_/g, " ")}`, href: `/expertise-exchange?request=${request.id}`, icon: Handshake });
  }
  for (const file of data.legislationFiles) {
    if (matches(`${file.title} ${file.summary} ${file.jurisdictionOrInstitution} ${file.stage}`)) rows.push({ kind: "Legislative action", title: file.title, detail: `${file.jurisdictionOrInstitution} · ${file.stage}`, href: `/legislative-action?file=${file.id}`, icon: Scale });
  }
  return rows.slice(0, 24);
}

function QuickLink({ to, icon: Icon, label, count }: { to: string; icon: typeof Bookmark; label: string; count?: number }) {
  return (
    <Link to={to} className="navlink">
      <Icon />
      <span className="navlabel">{label}</span>
      {count && count > 0 ? <span className="navcount">{count}</span> : null}
    </Link>
  );
}

function PinnedQuickLink({ pin, onRemove }: { pin: QuickAccessPin; onRemove: () => void }) {
  return (
    <div className="navlink" style={{ paddingRight: 4 }}>
      <Link to={pin.href} className="navlabel" style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0, color: "inherit" }} title={pin.label}>
        <Bookmark style={{ width: 17, height: 17, flex: "none" }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pin.label}</span>
      </Link>
      <button type="button" aria-label={`Remove ${pin.label}`} onClick={onRemove} style={{ border: "none", background: "none", color: "var(--muted)", cursor: "pointer", display: "grid", placeItems: "center", width: 22, height: 22 }}>
        <X style={{ width: 14, height: 14 }} />
      </button>
    </div>
  );
}

function MenuLink({ to, icon: Icon, label, onClick }: { to: string; icon: typeof UserRound; label: string; onClick?: () => void }) {
  return (
    <Link to={to} onClick={onClick} role="menuitem" className="btn-sm" style={{ width: "100%", justifyContent: "flex-start", border: "none", marginBottom: 2 }}>
      <Icon style={{ width: 16, height: 16 }} />
      {label}
    </Link>
  );
}

function MenuExternalLink({ href, icon: Icon, label, onClick }: { href: string; icon: typeof ExternalLink; label: string; onClick?: () => void }) {
  return (
    <a href={href} onClick={onClick} role="menuitem" className="btn-sm" style={{ width: "100%", justifyContent: "flex-start", border: "none", marginBottom: 2 }}>
      <Icon style={{ width: 16, height: 16 }} />
      {label}
    </a>
  );
}

function readDisplayTweaks(): { density: Density; accent: Accent } {
  if (typeof window === "undefined") return { density: "Comfortable", accent: "Burgundy" };
  try {
    const raw = window.localStorage.getItem(displayStorageKey);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      density: parsed?.density === "Compact" ? "Compact" : "Comfortable",
      accent: parsed?.accent === "Ink" ? "Ink" : "Burgundy",
    };
  } catch {
    return { density: "Comfortable", accent: "Burgundy" };
  }
}

function writeDisplayTweaks(value: { density: Density; accent: Accent }) {
  try {
    window.localStorage.setItem(displayStorageKey, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

function readQuickAccessPins(): QuickAccessPin[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(quickAccessStorageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is QuickAccessPin => typeof item?.href === "string" && typeof item?.label === "string").slice(0, 8);
  } catch {
    return [];
  }
}

function writeQuickAccessPins(pins: QuickAccessPin[]) {
  window.localStorage.setItem(quickAccessStorageKey, JSON.stringify(pins));
}

function quickPinsFromData(data: LexData): QuickAccessPin[] {
  return data.quickAccessPins
    .slice()
    .sort((left, right) => right.updatedAt - left.updatedAt)
    .map(({ href, label }) => ({ href, label }))
    .slice(0, 8);
}

function canPinQuickAccessPage(pathname: string, search: string) {
  if (builtInQuickAccessHrefs.includes(`${pathname}${search}`)) return false;
  if (search) return true;
  return !navItems.some((item) => item.to === pathname);
}

function labelForQuickAccessPage(pathname: string, search: string) {
  const params = new URLSearchParams(search);
  if (pathname.startsWith("/members/")) return "Member profile";
  if (pathname.startsWith("/discussions/proposals/")) return "Proposal record";
  if (pathname === "/my-workspace/delegations") return "Procurations";
  if (pathname === "/my-workspace" && params.get("view") === "drafts") return "My drafts";
  if (pathname === "/my-workspace" && params.get("view") === "actions") return "My actions";
  if (pathname === "/my-workspace" && params.get("view") === "following") return "Saved and followed";
  if (pathname === "/expertise-exchange" && params.get("tab")) return `Expertise ${params.get("tab")}`;
  return pathname.split("/").filter(Boolean).map((part) => part.replace(/-/g, " ")).join(" / ") || "Current page";
}
